import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Factory, ArrowLeft, ChevronRight, FolderOpen, Download,
  CheckCircle2, FileDown, Package,
} from "lucide-react";
import { toast } from "sonner";

/* Production user portal (Msg 945) — the production team's only job here is to
   download client-approved production files and produce. Per-file download +
   one-click "Download all" (ZIP), grouped by sub-scope and wall. */

async function blobDownload(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const objectUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = objectUrl; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default function ProductionPortal() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/production-board")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="production-portal-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} onBack={() => setCode(null)} />
      ) : (
        <ProjectList projects={projects} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="production-project-list">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center">
          <Factory size={20} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Production Files</h1>
          <p className="text-sm text-zinc-500">Open a project to download its approved production files.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl">
          No approved production files to download yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {projects.map((p) => {
            const pending = p.ready_count - p.done_count;
            return (
              <button key={p.project_code} onClick={() => onOpen(p.project_code)}
                data-testid={`production-project-${p.project_code}`}
                className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-orange-300 hover:shadow-md transition-all">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                  <ChevronRight size={16} className="text-zinc-300" />
                </div>
                <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.project_name}</div>
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <Package size={15} className="text-orange-500" />
                  <span className="text-zinc-600 font-medium">{p.ready_count} file(s) ready</span>
                  {pending > 0 && <span className="text-orange-600">· {pending} to produce</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectView({ project, onBack }) {
  const groups = {};
  const order = [];
  (project.lines || []).forEach((w) => {
    const g = w.group || "Items";
    if (!groups[g]) { groups[g] = []; order.push(g); }
    groups[g].push(w);
  });

  async function downloadOne(w) {
    try {
      await blobDownload(`/projects/${project.project_code}/boq/${w.boq_id}/production-file/download`, w.file_name);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Download failed"); }
  }
  async function downloadAll() {
    try {
      toast.success("Zipping this project's production files…");
      await blobDownload(`/production-files/download-all?code=${project.project_code}`, `production-files-${project.project_code}.zip`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Download failed"); }
  }

  return (
    <div data-testid="production-project-view">
      <button onClick={onBack} data-testid="production-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={project.project_code} /></span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.project_name}</h1>
        </div>
        <button onClick={downloadAll} data-testid="production-download-project"
          className="inline-flex items-center gap-2 rounded-xl bg-orange-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-orange-700 transition-colors shadow-sm">
          <FileDown size={16} /> Download all ({project.ready_count})
        </button>
      </div>

      <div className="space-y-6">
        {order.map((g) => (
          <div key={g} className="rounded-2xl border border-zinc-200 shadow-sm bg-white overflow-hidden"
            data-testid={`production-group-${g}`}>
            <div className="flex items-center gap-2 px-5 py-3 bg-zinc-50 border-b border-zinc-200">
              <FolderOpen size={18} className="text-orange-600" strokeWidth={1.6} />
              <span className="font-semibold text-zinc-900">{g}</span>
              <span className="text-xs text-zinc-400">· {groups[g].length} file(s)</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {groups[g].map((w) => (
                <div key={w.boq_id} className="flex items-center justify-between gap-3 px-5 py-3"
                  data-testid={`production-line-${w.boq_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-900 truncate flex items-center gap-2">
                      {w.wall_label}
                      {w.done && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                          <CheckCircle2 size={12} /> produced
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 truncate">{w.title} · {w.file_name}</div>
                  </div>
                  <button onClick={() => downloadOne(w)} data-testid={`production-download-${w.boq_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 text-orange-700 px-3 py-1.5 text-sm font-medium hover:bg-orange-50 transition-colors shrink-0">
                    <Download size={15} /> Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
